import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthUser } from '../../services/api/auth.types';
import { logger } from '../../utils/logger';
import settingsStyles from './AgentWidgetSettings.module.css';

interface AgentWidgetSettingsMenuProps {
  user: AuthUser | null | undefined;
  onOpenMicSettings: () => void;
  onOpenProfile: () => void;
  onCloseSettingsMenu: () => void;
  onLogout: () => Promise<void>;
}

export function AgentWidgetSettingsMenu({
  user,
  onOpenMicSettings,
  onOpenProfile,
  onCloseSettingsMenu,
  onLogout,
}: AgentWidgetSettingsMenuProps) {
  const { t } = useTranslation();

  const handleLogout = useCallback(async () => {
    onCloseSettingsMenu();
    try {
      await onLogout();
    } catch (error) {
      logger.error('[Widget] Logout failed:', error);
    }
  }, [onCloseSettingsMenu, onLogout]);

  return (
    <div className={settingsStyles.settingsMenu}>
      <div className={settingsStyles.settingsUserInfo}>
        <span className={settingsStyles.settingsUserName}>{user?.displayName || 'Agent'}</span>
        <span className={settingsStyles.settingsUserEmail}>{user?.username || ''}</span>
      </div>
      <div className={settingsStyles.settingsDivider} />
      <button
        type="button"
        className={settingsStyles.settingsMenuItem}
        onClick={() => {
          onOpenProfile();
          onCloseSettingsMenu();
        }}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
        {t('nav:profile')}
      </button>
      <button
        className={settingsStyles.settingsMenuItem}
        onClick={() => {
          onOpenMicSettings();
          onCloseSettingsMenu();
        }}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
        </svg>
        {t('agent:menu.audioSettings')}
      </button>
      <div className={settingsStyles.settingsDivider} />
      <button
        className={`${settingsStyles.settingsMenuItem} ${settingsStyles.logoutMenuItem}`}
        onClick={handleLogout}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
        </svg>
        {t('common:buttons.logout')}
      </button>
    </div>
  );
}
